import FeatureGates, {
  type GetExperimentValueOptions,
} from '@atlassian/feature-gate-js-client';

export const experimentConfig = {
  /**
   * An A/B test for 4 initial templates
   * https://switcheroo.atlassian.com/ui/gates/fac22772-0173-40ae-bd94-83e80d9da925
   */
  templatesM1: {
    experimentId: 'databases-frontend-templates-experiment',
    defaultParams: {
      enabled: false,
    },
  },
} satisfies Record<string, Experiment>;

type Experiment = {
  experimentId: string;
  defaultParams: Record<string, any>;
};

type ExperimentConfig = typeof experimentConfig;
type ExperimentName = keyof ExperimentConfig;
type ExperimentParams = {
  [key in keyof ExperimentConfig]: ExperimentConfig[key]['defaultParams'];
};

/**
 * Returns the value of a given parameter for the provided experiment
 *
 * **NOTE**: Statsig will automatically send an exposure event when this is called, unless you provide
 * `{ fireExperimentExposure: false }` to `options`
 *
 * @example getExperimentValue('templatesM1', 'enabled')
 * @example getExperimentValue('anotherExperiment', 'btnColor', { fireExperimentExposure: false })
 */
export const getExperimentValue = <
  ExpName extends ExperimentName,
  ParamName extends keyof ExperimentParams[ExpName],
  ParamType extends ExperimentParams[ExpName][ParamName]
>(
  experimentName: ExpName,
  paramName: ParamName,
  options?: GetExperimentValueOptions<ParamType>
): ParamType => {
  const { experimentId, defaultParams } = experimentConfig[experimentName];

  // Typescript is unable to discriminate between experiment params
  // This should be okay to cast as TypeScript will throw an error if we try to call this
  // function with a param name that doesn't exist for the provided experiment
  const defaultParam = (defaultParams as any)[paramName] as ParamType;

  return FeatureGates.getExperimentValue(
    experimentId,
    paramName as string,
    defaultParam,
    options
  );
};
